import { cn } from '@/app/lib/utils';
import * as React from 'react';

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'suffix'> & {
  label?: React.ReactNode;
  showAsterisk?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  preventPrefixClick?: boolean;
  wrapperClassName?: string;
  prefixWrapperClassName?: string;
  suffixWrapperClassName?: string;
  labelClassName?: string;
};

const baseInputClassName =
  'flex h-[38px] w-full rounded-md border border-input bg-white dark:bg-input/30 px-3 py-2 text-base ring-offset-background transition-all duration-200 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      showAsterisk = false,
      preventPrefixClick = true,
      id,
      wrapperClassName,
      prefix,
      suffix,
      prefixWrapperClassName,
      suffixWrapperClassName,
      labelClassName,
      ...props
    },
    ref
  ) => {
    const inputId = React.useId();
    const finalId = id || inputId;
    const hasInputGroup = !!prefix || !!suffix || !!wrapperClassName;
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const inputHandlers = {
      onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
        if (type === 'number') {
          e.currentTarget.blur();
        }
        props.onWheel?.(e);
      },
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (type === 'number' && e.key === 'e') {
          e.preventDefault();
        }
        props.onKeyDown?.(e);
      },
    };

    const labelElement = label ? (
      <label
        htmlFor={finalId}
        className={cn('text-sm font-medium text-foreground mb-1.5 flex', labelClassName)}
      >
        {label}
        {showAsterisk && <span className="text-red-600">&nbsp;*</span>}
      </label>
    ) : null;

    if (!hasInputGroup) {
      return (
        <>
          {labelElement}
          <input
            id={finalId}
            type={type}
            className={cn(
              baseInputClassName,
              type === 'number' &&
                '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [appearance:textfield]',
              className
            )}
            ref={inputRef}
            autoComplete="off"
            {...props}
            {...inputHandlers}
          />
        </>
      );
    }

    return (
      <>
        {labelElement}
        <div
          className={cn(
            'input-group flex h-[38px] items-center overflow-hidden relative rounded-md border border-input bg-white dark:bg-input/30 ring-offset-background transition-all duration-200 focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 has-disabled:cursor-not-allowed has-disabled:opacity-50 min-w-0 max-w-full w-full',
            wrapperClassName
          )}
        >
          {prefix && (
            <div
              className={cn(
                'input-group-addon py-2 px-3 flex items-center text-muted-foreground',
                preventPrefixClick ? 'pointer-events-none absolute' : '',
                prefixWrapperClassName
              )}
              onClick={() => inputRef.current?.focus()}
            >
              {prefix}
            </div>
          )}
          <input
            id={finalId}
            type={type}
            className={cn(
              // strip shared input's own border/ring/rounded/bg so the wrapper handles them
              'h-full min-h-0 w-full flex-1 min-w-0 max-w-full rounded-none border-0 bg-transparent py-2 px-3 font-normal text-sm text-foreground placeholder:text-muted-foreground',
              'outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
              'disabled:bg-transparent',
              prefix ? 'pl-9' : 'pl-[14px]',
              type === 'number' &&
                '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [appearance:textfield]',
              className
            )}
            ref={inputRef}
            autoComplete="off"
            {...props}
            {...inputHandlers}
          />
          {suffix && (
            <span
              className={cn(
                'input-group-addon py-2 px-3 text-muted-foreground',
                suffixWrapperClassName
              )}
            >
              {suffix}
            </span>
          )}
        </div>
      </>
    );
  }
);

Input.displayName = 'Input';
export { Input };
export default Input;
